import { IMAGES } from "@/lib/site";
import { FadeUp, MaskedLine } from "@/components/Reveal";

const PRINCIPLES = [
  {
    icon: "/images/icons/pillar-hands.png",
    title: "Therapeutic",
    text: "Techniques grounded in anatomy, focused on relieving tension and restoring balance.",
  },
  {
    icon: "/images/icons/pillar-body.png",
    title: "Body Awareness",
    text: "Understanding your body’s unique patterns and needs to create effective, personalized care.",
  },
  {
    icon: "/images/icons/pillar-waves.png",
    title: "Nervous System Support",
    text: "Helping the body shift from stress and tension toward a calmer, more balanced state.",
  },
  {
    icon: "/images/icons/pillar-diamond.png",
    title: "Quality & Care",
    text: "Professional products carefully selected for quality and comfort.",
  },
  {
    icon: "/images/icons/pillar-crown.png",
    title: "Royal Experience",
    text: "An experience created to make you feel seen, valued, and welcome from the moment you arrive.",
  },
];

const Approach = () => (
  <section
    id="approach"
    data-chapter="03"
    data-testid="approach-section"
    className="relative overflow-hidden bg-forest py-20 text-ivory lg:py-28"
  >
    <div
      className="pointer-events-none absolute -right-32 top-0 h-full w-[640px] opacity-[0.1]"
      style={{
        backgroundImage: `url(${IMAGES.leavesShadow})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        WebkitMaskImage: "radial-gradient(closest-side, black 30%, transparent 100%)",
        maskImage: "radial-gradient(closest-side, black 30%, transparent 100%)",
      }}
      aria-hidden="true"
    />
    <div className="relative mx-auto max-w-[1500px] px-6 lg:px-14">
      <FadeUp className="mb-6 flex items-center gap-5">
        <span className="font-sans text-[11px] font-medium uppercase tracking-[0.4em] text-gold">03 — Our Approach</span>
        <span className="h-px w-24 bg-gold/60" />
      </FadeUp>
      <h2 className="mb-14 font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="approach-headline">
        <MaskedLine>Five pillars.</MaskedLine>
        <MaskedLine delay={0.12}>
          <span className="italic text-sand">One quiet intention.</span>
        </MaskedLine>
      </h2>

      <div className="grid gap-y-12 sm:grid-cols-2 lg:grid-cols-5 lg:gap-y-0">
        {PRINCIPLES.map(({ icon, title, text }, i) => (
          <FadeUp
            key={title}
            delay={0.08 + i * 0.07}
            className={`group flex flex-col items-center text-center lg:px-7 ${i > 0 ? "lg:border-l lg:border-ivory/10" : ""}`}
            data-testid={`approach-principle-${i}`}
          >
            <img
              src={icon}
              alt=""
              className="h-16 w-auto object-contain opacity-90 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100 lg:h-20"
              aria-hidden="true"
            />
            <h3 className="mt-6 font-serif text-2xl font-light leading-snug tracking-wide">{title}</h3>
            <p className="mt-4 max-w-[28ch] font-sans text-[13px] font-light leading-relaxed tracking-wide text-ivory/70">{text}</p>
            <span className="mt-6 block h-px w-12 rounded-full bg-gold/50 transition-all duration-700 group-hover:w-16 group-hover:bg-gold" aria-hidden="true" />
          </FadeUp>
        ))}
      </div>

      <FadeUp delay={0.3} className="mt-16 flex flex-col items-center justify-center gap-5" data-testid="everybody-welcome">
        <svg width="48" height="25" viewBox="0 0 30 16" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="infbow" x1="0" y1="0" x2="30" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#E40303" />
              <stop offset="0.2" stopColor="#FF8C00" />
              <stop offset="0.4" stopColor="#FFED00" />
              <stop offset="0.6" stopColor="#008026" />
              <stop offset="0.8" stopColor="#24408E" />
              <stop offset="1" stopColor="#732982" />
            </linearGradient>
          </defs>
          <path
            d="M15 8 C15 4.7 12.3 2.5 9 2.5 C5.7 2.5 3 4.7 3 8 C3 11.3 5.7 13.5 9 13.5 C12.3 13.5 15 11.3 15 8 C15 11.3 17.7 13.5 21 13.5 C24.3 13.5 27 11.3 27 8 C27 4.7 24.3 2.5 21 2.5 C17.7 2.5 15 4.7 15 8 Z"
            stroke="url(#infbow)"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
        <p className="text-center font-sans text-xs font-light uppercase tracking-[0.3em] text-ivory/60">
          A space where <span className="text-ivory">every body</span> is welcome
        </p>
      </FadeUp>
    </div>
  </section>
);

export default Approach;
